import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@project/lib/data-client";
import { PageShell } from "@project/user/components/page-shell";
import { Button } from "@project/components/ui/button";
import {
  CheckCircle2,
  Heart,
  Home,
  Share2,
  Download,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { downloadReceiptPDF } from "@project/lib/receipt-pdf";

export default function ThankYou() {
  const { ref } = Route.useParams();
  const [donation, setDonation] = useState<any>(null);
  const [donor, setDonor] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data: d } = await supabase
        .from("donations")
        .select("*")
        .eq("receipt_no", ref)
        .maybeSingle();
      setDonation(d);
      if (d?.donor_id) {
        const { data: dr } = await supabase
          .from("donors")
          .select(
            "full_name, mobile, email, address, city, state, pincode, pan"
          )
          .eq("id", d.donor_id)
          .maybeSingle();
        setDonor(dr);
      }
      setLoading(false);
    })();
  }, [ref]);

  const handleDownload = () => {
    if (!donation) {
      return;
    }
    try {
      downloadReceiptPDF({
        receipt_no: donation.receipt_no,
        donation_date: donation.donation_date,
        amount: Number(donation.amount),
        payment_mode: donation.payment_mode,
        transaction_id: donation.transaction_id,
        purpose: donation.purpose,
        category: donation.category,
        requires_80g: donation.requires_80g,
        donor,
      });
      toast.success("Receipt downloaded");
    } catch (e: any) {
      toast.error(e?.message ?? "Could not generate receipt");
    }
  };

  const recognition =
    donation?.recognition_name ?? donation?.family_display_name;

  return (
    <PageShell>
      <div className="mx-auto max-w-2xl px-4 py-16">
        <div className="border-gold/30 anim-zoom-in rounded-3xl border bg-card p-10 text-center shadow-lg">
          <div
            className="bg-gold/15 anim-zoom-in mx-auto grid h-20 w-20 place-items-center rounded-full text-gold"
            style={{ animationDelay: "250ms" }}
          >
            <CheckCircle2 className="h-12 w-12" />
          </div>
          <h1
            className="anim-fade-up mt-6 font-serif text-3xl font-bold text-primary md:text-4xl"
            style={{ animationDelay: "380ms" }}
          >
            Thank You!
          </h1>
          <p
            className="anim-fade-up mt-3 text-muted-foreground"
            style={{ animationDelay: "480ms" }}
          >
            Your generous contribution has been recorded.
          </p>

          {recognition && (
            <div className="border-gold/40 bg-gold/10 mx-auto mt-5 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-semibold text-primary">
              🏷️ Recognised as: {recognition}
            </div>
          )}

          {loading ? (
            <div className="mt-8 grid place-items-center">
              <Loader2 className="h-6 w-6 animate-spin text-gold" />
            </div>
          ) : (
            <div className="bg-muted/30 anim-fade-up mt-6 rounded-2xl border border-border p-5 text-left">
              <Row
                label="Receipt No."
                value={donation?.receipt_no ?? ref}
                mono
              />
              {donor?.full_name && (
                <Row label="Donor" value={donor.full_name} />
              )}
              {donor?.mobile && <Row label="Mobile" value={donor.mobile} />}
              {donor?.email && <Row label="Email" value={donor.email} />}
              {donor?.pan && <Row label="PAN" value={donor.pan} />}
              {donation && (
                <>
                  <Row
                    label="Amount"
                    value={`₹${Number(donation.amount).toLocaleString("en-IN")}`}
                  />
                  {donation.purpose && (
                    <Row label="Cause" value={donation.purpose} />
                  )}
                  {donation.frequency && donation.frequency !== "one-time" && (
                    <Row label="Frequency" value={donation.frequency} />
                  )}
                  <Row
                    label="Date"
                    value={new Date(donation.donation_date).toLocaleDateString(
                      "en-IN",
                      { day: "numeric", month: "long", year: "numeric" }
                    )}
                  />
                  {donation.seva_date && (
                    <Row
                      label="Seva Date"
                      value={new Date(donation.seva_date).toLocaleDateString(
                        "en-IN",
                        { day: "numeric", month: "long", year: "numeric" }
                      )}
                    />
                  )}
                  {donation.in_memory_of && (
                    <Row label="In memory of" value={donation.in_memory_of} />
                  )}
                  {donation.occasion && (
                    <Row label="Occasion" value={donation.occasion} />
                  )}
                  {donation.payment_mode && (
                    <Row label="Payment Mode" value={donation.payment_mode} />
                  )}
                  {donation.transaction_id && (
                    <Row
                      label="Transaction ID"
                      value={donation.transaction_id}
                      mono
                    />
                  )}
                  <Row
                    label="Status"
                    value={
                      donation.payment_status === "success"
                        ? "Confirmed"
                        : "Pending verification"
                    }
                  />
                </>
              )}
            </div>
          )}

          <p className="mt-6 text-sm text-muted-foreground">
            {donation
              ? "Our team will verify your payment and email an official 80G receipt within 24 hours. You can also download a copy now."
              : "Your online payment was successful. An official receipt will be emailed to you shortly."}
          </p>

          <div
            className="anim-fade-up mt-8 flex flex-wrap items-center justify-center gap-3"
            style={{ animationDelay: "600ms" }}
          >
            <Button
              onClick={handleDownload}
              disabled={!donation}
              className="hover:bg-gold/90 btn-lift gap-2 bg-gold text-gold-foreground"
            >
              <Download className="h-4 w-4" /> Download Receipt
            </Button>
            <Button asChild variant="outline" className="gap-2">
              <Link to="/">
                <Home className="h-4 w-4" /> Home
              </Link>
            </Button>
            <Button asChild variant="outline" className="gap-2">
              <Link to="/donate">
                <Heart className="h-4 w-4" /> Donate Again
              </Link>
            </Button>
            <Button
              variant="ghost"
              className="gap-2"
              onClick={() => {
                navigator.clipboard.writeText(
                  `I just supported Jivandhara Old Age Home. Reference ${ref}. Join me: ${window.location.origin}/donate`
                );
                toast.success("Share message copied");
              }}
            >
              <Share2 className="h-4 w-4" /> Share
            </Button>
          </div>
        </div>
      </div>
    </PageShell>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="border-border/50 flex items-center justify-between gap-3 border-b py-2 last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span
        className={`text-sm font-semibold text-foreground ${mono ? "font-mono" : ""}`}
      >
        {value}
      </span>
    </div>
  );
}
